import { Component, EventEmitter, Input, OnDestroy, Output, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { ITableKey } from 'src/app/modules/shared/components/basic-table/basic-table.constants';
import { EPolicyCompany, EPolicyStatus, IPolicyAsset } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { EProductType, MULTISOLUTION_PRODUCTS, MULTISTRATEGY_PRODUCTS } from '../../../athora/athora.constants';
import { NEED_PERCENTAGE_BOX_FIELDS } from 'src/app/modules/isv/isv.constants';

@Component({
  selector: 'app-assets-table',
  templateUrl: './assets-table.component.html',
  styleUrl: './assets-table.component.scss',
})
export class AssetsTableComponent implements OnInit, OnDestroy {

  @Input() form: FormGroup;
  @Input() assets: IPolicyAsset[] = [];
  @Input() tableKeys: ITableKey[];
  @Input() divestedFunds: string[];
  @Input() isReadOnly: boolean;
  @Input() shouldWatchAmount: boolean = true;
  @Output() emitFunds = new EventEmitter<IPolicyAsset[]>();

  disableAssets: boolean;
  amountSub: any;

  destroy$ = new Subject();

  constructor (
    private _policyService: PolicyService
  ) { }

  ngOnInit(): void {
    if ([EPolicyCompany.ISV, EPolicyCompany.ISLD].includes(this._policyService.companyId as EPolicyCompany) && 
    ['_VAGG', '_RISPA'].includes(this._policyService.operationId)) {
        const typeControl = this.form.get('type'); 
        if (typeControl) {
            this.disableAssets = !typeControl.value;
            typeControl.value ? this.totalAmountCtrl.disable() : this.totalAmountCtrl.enable();
        } else {
            this.disableAssets = true;
        }
    }

    this.manageForm();
  }

  get assetAmountIsDisabled(): boolean {
    if (this._policyService.isDetailPrevidenziale && this._policyService.operationId !== '_SWITR') {
      return true;
    } else if ([EPolicyCompany.ISV, EPolicyCompany.ISLD].includes(this._policyService.companyId as EPolicyCompany)) {
      return this.disableAssets;
    }
    return MULTISOLUTION_PRODUCTS.includes(EProductType[this._policyService.productCode]);
  }

  get totalAmountCtrl(): AbstractControl {
    return this.form.get('amount');
  }

  get isAdditionalPayment(): boolean {
    return this._policyService.operationId === '_VAGG';
  }

  get isFormDisabled(): boolean {
    return this.form && this.form.disabled;
  }

  maxAmountLimit(fund: IPolicyAsset): boolean {
    let fundAmount = fund?.fundAmount?.split('.').join('');
    fundAmount = fundAmount?.replace(',', '.');
    let amount = fund?.amount?.split('.').join('');
    amount = amount?.replace(',', '.');
    return fund && !this.isAdditionalPayment && (parseFloat(amount) > parseFloat(fundAmount));
  }

  manageForm(): void {
    if (!this.form?.get('amount')) return;
    if (!this.shouldWatchAmount) return;
    if (this.assets && this.assets.length > 0 &&
        [EPolicyCompany.ISV, EPolicyCompany.ISLD].includes(this._policyService.companyId as EPolicyCompany) && 
        ['_VAGG', '_RISPA'].includes(this._policyService.operationId)
    ) {
      this.watchModifyAssetsToggle();
      this.watchAmount();
    } else {
      if (this.assetAmountIsDisabled && !this.form.disabled) {
        this.totalAmountCtrl.enable();
        this.watchAmount();
      } else {
        this.totalAmountCtrl.disable();
        if (this.assets) {
          this.assets.forEach(asset => {
            let amount = asset.amount?.replace(/\./g, '');
            amount = amount?.replace(',', '.');
            amount = parseFloat(amount);
            if (!isNaN(amount)) {
              asset.amount = amount.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
            } else {
              asset.amount = '0,00';
            }
          });
        }
    }
    }
  }

  refreshAssets(totalAmount: string): void {
    const totalAmountNumber = parseFloat(totalAmount.replace(',', '.'));
    if (isNaN(totalAmountNumber)) {
      LoggerService.logError(this.constructor.name, 'refreshAssets', 'totalAmountNumber isNaN');
      return;
    }
    let remainingAmount = totalAmountNumber;
    this.assets.forEach(asset => {
      let amount = totalAmountNumber * (asset.percentage / 100);
      // Workaround for the case where the total amount is less than the sum of the percentages
      // TODO: Determine whether this should be global logic or specific to ISV policies
      if (this.isIsvPolicy) {
        if (remainingAmount < amount) {
          amount = remainingAmount;
        } else {
          remainingAmount -= amount;
        }
      }
      if (!isNaN(amount)) {
        asset.amount = amount.toLocaleString('it-IT', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      } else {
        asset.amount = '0,00';
      }
    });
    LoggerService.logInfo(this.constructor.name, 'refreshAssets', { assets: this.assets });
  }

  formatAssetAmount(event: Event, asset: IPolicyAsset): void {
    if (asset) {
      const input = event.target as HTMLInputElement;
      let rawValue = asset.amount;
      rawValue = rawValue?.replace(/\./g, '');
      rawValue = rawValue?.replace(',', '.');
      const number = parseFloat(rawValue);
      if (!isNaN(number)) {
        const formatted = number.toLocaleString('it-IT', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        input.value = formatted;
      } else {
        input.value = '';
      }
      this.emitFunds.emit(this.assets);
    }
  }

  formatPercentageAmount(event: Event, asset: IPolicyAsset, key: string): void {
    if (asset) {
      const input = event.target as HTMLInputElement;
      const value = parseFloat(input.value.replace(',', '.'));
      if (!value || isNaN(value)) {
        asset[key] = '0';
        return;
      }
      if (value > 100) {
        asset[key] = '100';
      } else if (value < 0) {
        asset[key] = '0';
      } else {
        input.value = value.toString().replace('.', ',');
      }
    }
  }

  refreshTotalAmount() {
    let sum = 0;
    this.assets.forEach(asset => {
      let assetAmount = asset.amount ? asset.amount: '0,00';
      assetAmount = assetAmount.replace(/\./g, '');
      assetAmount = assetAmount.replace(',', '.');
      sum += parseFloat(assetAmount);
    });
    const totalAmountValue = sum.toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    this.totalAmountCtrl.setValue(totalAmountValue);
  }

  watchAmount(): void {
    if (this.amountSub) {
      this.amountSub.unsubscribe();
      this.amountSub = null;
    }
    this.amountSub = this.totalAmountCtrl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        filter(x => !!x)
      )
      .subscribe(value => {
        this.refreshAssets(value);
      });
  }

  watchModifyAssetsToggle(): void {
    this.form?.get('type')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe(value => {
        if (value) {
          this.totalAmountCtrl.setValue('0');
          this.disableAssets = false;
          this.totalAmountCtrl.disable();
          this.refreshAssets(this.totalAmountCtrl.value);
          if (this.amountSub) {
            this.amountSub.unsubscribe();
            this.amountSub = null;
          }
        } else {
          this.disableAssets = true;
          this.totalAmountCtrl.enable();
          this.watchAmount();
        }
      });
  }

  checkIfDisabled(fund: IPolicyAsset, key?: string): boolean {
    if (this.isReadOnly || (this.assetAmountIsDisabled && key === 'amount')) {
      return true;
    }
    if (this._policyService.operationId === 'INT_008') {
      return this.form.get(key)?.value === 0;
    }
    if (fund && this._policyService.operationId === '_SWIT' && MULTISTRATEGY_PRODUCTS.includes(EProductType[this._policyService.productCode])) {
      return this.divestedFunds && this.divestedFunds.includes(fund.lineCode);
    }
    if (fund && this._policyService.operationId === '_SWITR' && this.isIsvPolicy) {
      return this.divestedFunds && this.divestedFunds.includes(fund.fundCode);
    }
    return false;
  }

  formatValue(value: string): string {
    return value.replace('.', ',');
  }
  get isIsvPolicy(): boolean {
    return CommonUtils.getCompanyCode(this._policyService.companyId) === 'isv';
  }

  needPercentageBox(key: string): boolean {
    return NEED_PERCENTAGE_BOX_FIELDS.includes(key);
  }
  ngOnDestroy(): void {
    if (this.amountSub) {
      this.amountSub.unsubscribe();
      this.amountSub = null;
    }
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
